import React from 'react';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField } from '@mui/material';
import { FastField } from 'formik';
import moment from 'moment';
import PropTypes from 'prop-types';

const pickerDialogSx = {
  '& .MuiDialog-paper': {
    width: 320,
    maxWidth: 320,
    borderRadius: '2px',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
    overflow: 'visible',
  },
  '& .MuiPickersToolbar-root': {
    minHeight: 82,
    padding: '18px 24px 16px',
    backgroundColor: '#B72B5F',
    color: '#FFFFFF',
  },
  '& .MuiPickersToolbar-root .MuiTypography-overline': {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.3,
    letterSpacing: 0,
    textTransform: 'none',
  },
  '& .MuiDatePickerToolbar-title': {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.2,
    marginTop: 2,
    textAlign: 'left',
  },
  '& .MuiPickersToolbar-penIconButton': {
    display: 'none',
  },
  '& .MuiCalendarPicker-root': {
    width: 320,
    height: 316,
    maxHeight: 316,
  },
  '& .MuiPickersCalendarHeader-root': {
    margin: 0,
    padding: '8px 12px 0',
    minHeight: 48,
    maxHeight: 48,
  },
  '& .MuiPickersCalendarHeader-labelContainer': {
    margin: '0 auto',
    fontSize: 13,
    fontWeight: 400,
  },
  '& .MuiPickersCalendarHeader-switchViewButton': {
    display: 'none',
  },
  '& .MuiPickersArrowSwitcher-root': {
    justifyContent: 'space-between',
    position: 'absolute',
    left: 12,
    right: 12,
    pointerEvents: 'none',
  },
  '& .MuiPickersArrowSwitcher-button': {
    pointerEvents: 'auto',
    color: '#546E7A',
  },
  '& .MuiDayPicker-header': {
    justifyContent: 'space-around',
    padding: '0 12px',
  },
  '& .MuiDayPicker-weekDayLabel': {
    width: 34,
    height: 28,
    margin: '0 2px',
    color: '#9E9E9E',
    fontSize: 11,
    fontWeight: 400,
  },
  '& .MuiDayPicker-slideTransition': {
    minHeight: 240,
  },
  '& .MuiDayPicker-weekContainer': {
    justifyContent: 'space-around',
    margin: 0,
    padding: '0 12px',
  },
  '& .MuiPickersDay-root': {
    width: 34,
    height: 34,
    margin: '0 2px',
    fontSize: 12,
    color: '#263238',
  },
  '& .MuiPickersDay-root.Mui-selected': {
    backgroundColor: '#B72B5F',
    color: '#FFFFFF',
  },
  '& .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus':
    {
      backgroundColor: '#9E2652',
    },
  '& .MuiDialogActions-root': {
    minHeight: 52,
    padding: '8px 12px',
    justifyContent: 'space-between',
  },
  '& .MuiDialogActions-root .MuiButton-root': {
    color: '#B72B5F',
    fontSize: 12,
    fontWeight: 700,
    minWidth: 64,
  },
};

const FormikDatePicker = React.memo((props) => {
  return (
    <FastField
      {...props}
      component={({ form, field }) => {
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileDatePicker
              {...props}
              disabled={props.disabled || form.isSubmitting}
              label={props.label}
              value={props.value ? props.value : null}
              onChange={(date) => {
                form.setFieldValue(
                  props.name,
                  date ? moment(date).format('YYYY-MM-DD') : '',
                );
              }}
              inputFormat={props.format}
              showToolbar
              toolbarTitle={moment(props.value || undefined).format('YYYY')}
              toolbarFormat="EEE, MMM d"
              DialogProps={{ sx: pickerDialogSx }}
              componentsProps={{
                actionBar: {
                  actions: ['clear', 'cancel', 'accept'],
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  margin={props.margin}
                  size={props.size}
                  variant="outlined"
                  error={Boolean(form.errors[field.name])}
                  helperText={form.errors[field.name]}
                />
              )}
            />
          </LocalizationProvider>
        );
      }}
    />
  );
});

FormikDatePicker.propTypes = {
  disabled: PropTypes.bool,
  format: PropTypes.string,
  label: PropTypes.string.isRequired,
  margin: PropTypes.string,
  name: PropTypes.string.isRequired,
  size: PropTypes.string,
};

FormikDatePicker.defaultProps = {
  format: 'yyyy-MM-dd',
  margin: 'none',
  size: 'medium',
};

export default FormikDatePicker;
